import { Sparkles, X } from "lucide-react";

import type { AuditEvent } from "../../../models/audit/auditEvent";
import { cn } from "../../../lib/utils";
import {
  AuditEventDetail,
  hasDetail,
  runScopeSummary,
} from "./AuditEventDetail";
import { AuditEventGlyph } from "./AuditEventGlyph";
import { formatFullTimestamp } from "./auditDates";
import { isAutomatedExecution, resolveActor } from "./resolveActor";

const hasText = (value: string | null | undefined): value is string =>
  !!value && value.trim().length > 0;

/** A control's test. One per control by construction; the schema doesn't
 * enforce that, so extras are listed rather than silently dropped — an audit
 * surface should show the anomaly, not hide it. */
const controlTest = (names: string[] | undefined): string | null => {
  const present = (names ?? []).filter(hasText);
  return present.length > 0 ? present.join(", ") : null;
};

interface AuditEventDetailPaneProps {
  event: AuditEvent;
  onClose: () => void;
  memberEmailById?: (userId: string) => string | undefined;
  columnNameById?: (columnId: string) => string | undefined;
  /** True when the surrounding surface is already scoped to a single test
   * (the session drawer, whose header names it) — drops the `test` scope
   * field so the pane only states scope the surface doesn't. The
   * workspace-wide log mixes tests and leaves this false. */
  scopedToTest?: boolean;
}

/**
 * Everything known about one event, opened alongside the timeline rail.
 *
 * Layout: title over a labeled scope list (control / test / sample /
 * attribute / document, populated entries only), then a compact provenance
 * strip — who triggered it, the AI-execution pill, the full timestamp —
 * kept at the top where an auditor looks first, then the change/diff content
 * with the rest of the pane to itself.
 *
 * Scope and subject are kept disjoint: the field an event is *about* leaves
 * the header list and leads the body instead, so nothing is stated twice.
 * Reading here never disturbs the rail; the close control dismisses this
 * pane only, returning the drawer to the single-column feed.
 */
export function AuditEventDetailPane({
  event,
  onClose,
  memberEmailById,
  columnNameById,
  scopedToTest = false,
}: AuditEventDetailPaneProps) {
  const actor = resolveActor(event, memberEmailById);
  const automated = isAutomatedExecution(event);

  // What the event is *about* belongs in the body, not the header: a sample
  // rerun is not "an event scoped to Sample 3", it is the thing that happened
  // to Sample 3. Those events drop the field from the header scope below and
  // state it as the body's leading line, so no field is ever shown twice.
  const subject =
    event.eventType === "sample_rerun"
      ? { label: "sample", value: runScopeSummary(event, columnNameById) }
      : event.eventType === "column_rerun"
        ? { label: "column", value: runScopeSummary(event, columnNameById) }
        : null;
  const subjectValue = hasText(subject?.value) ? subject : null;

  // Header scope: where the event happened, one labeled field per populated
  // value. The test is dropped where the surrounding surface already names it
  // (the session drawer), and whichever field the event is about is dropped
  // in favour of the body line above.
  const scopeFields: { label: string; value: string }[] = [
    { label: "control", value: event.controlName },
    // One field, two sources: events scoped to a session name it directly;
    // control-level events (config edits) have none, so the control's own
    // test stands in. Mutually exclusive — the feed only sends the latter
    // where there is no session — and its absence is what marks a template.
    {
      label: "test",
      value: scopedToTest
        ? null
        : (event.testName ?? controlTest(event.currentControlTestNames)),
    },
    {
      label: "sample",
      value: subject?.label === "sample" ? null : event.sampleLabel,
    },
    {
      label: "attribute",
      value: subject?.label === "column" ? null : event.attributeLabel,
    },
    {
      label: "document",
      value: event.document?.documentNames?.join(", ") ?? null,
    },
  ].flatMap(({ label, value }) => (hasText(value) ? [{ label, value }] : []));

  // Stored titles carry the cell scope ("Reran Cell: Invoice Amount — Sample
  // 2") so the rail is self-contained, but here the labeled fields already say
  // it — strip the scope suffix from the heading rather than show it twice.
  const scopeSuffix = [event.attributeLabel, event.sampleLabel]
    .filter(hasText)
    .join(" — ");
  const title =
    scopeSuffix && event.title.endsWith(scopeSuffix)
      ? event.title
          .slice(0, event.title.length - scopeSuffix.length)
          .replace(/(: | — )$/, "")
      : event.title;
  // The subject line is body content in its own right: a sample rerun with no
  // diff still has to say which sample, so the body can't be gated on payload
  // detail alone.
  const showBody = hasDetail(event) || subjectValue !== null;

  return (
    <div className="flex h-full flex-col">
      {/* Bodiless panes end at the provenance row: bottom padding shrinks to
          match the row's top padding so the row sits centered between the
          header hairline and the footer border. */}
      <div
        className={cn(
          "flex-1 space-y-4 overflow-y-auto px-6 pt-5",
          showBody ? "pb-5" : "pb-3",
        )}
      >
        <div className="flex items-start gap-3">
          <AuditEventGlyph
            eventType={event.eventType}
            className="mt-0.5 border-stone-200"
          />
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold leading-snug text-stone-900">
              {title}
            </h3>
            {scopeFields.length > 0 && (
              <dl className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5">
                {scopeFields.map(({ label, value }) => (
                  <div key={label} className="contents">
                    <dt className="text-[11px] leading-5 text-stone-400">
                      {label}
                    </dt>
                    <dd
                      title={value}
                      className="truncate text-xs leading-5 text-stone-600"
                    >
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="-mr-1 -mt-1 rounded-md p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Bottom hairline and padding only when detail content follows —
            without a body the hairline would stack against the footer's top
            border as a double line, and the padding would stack with the
            container's into a dead gap. */}
        <div
          className={cn(
            "flex flex-wrap items-center gap-2 border-t border-stone-200/70 pt-3",
            showBody && "border-b pb-3",
          )}
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-100 text-[10px] font-medium uppercase text-stone-600">
            {actor.slice(0, actor === "AI" ? 2 : 1)}
          </span>
          <span className="truncate text-xs font-medium text-stone-700">
            {actor}
          </span>
          {automated && (
            <span className="flex shrink-0 items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-500">
              <Sparkles className="h-2.5 w-2.5" />
              Run by AI
            </span>
          )}
          <span className="ml-auto shrink-0 rounded-full bg-stone-100 px-2.5 py-0.5 text-[10px] tabular-nums text-stone-500">
            {formatFullTimestamp(new Date(event.createdAt))}
          </span>
        </div>

        {subjectValue && (
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] uppercase tracking-wide text-stone-400">
              {subjectValue.label}
            </span>
            <span className="min-w-0 truncate text-sm text-stone-800">
              {subjectValue.value}
            </span>
          </div>
        )}

        {hasDetail(event) && (
          <AuditEventDetail event={event} memberEmailById={memberEmailById} />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2 border-t border-stone-200/70 px-6 py-3">
        <span
          title={`event ${event.id}`}
          className="ml-auto truncate font-mono text-[11px] text-stone-400"
        >
          event {event.id}
        </span>
      </div>
    </div>
  );
}
